import React, { useRef, useEffect, useState } from 'react';
import styled from 'styled-components/macro';

import { fontkoKR } from '@mirinae/react-ui';
import { os } from '@mirinae/js-utils/utils/userAgent';

// import PlayerContext from '@mirinae/lessons/modules/contexts/player';
import Element from '@mirinae/lessons/components/lesson/elements/Element';
import { FormattedText } from '@mirinae/lessons/components/lesson/elements/Text';
import { LessonButton } from '@mirinae/lessons/modules/defines/styles';
import { lessonsPalette, zIndex } from '@mirinae/lessons/modules/defines/styles';

import SongSVG from '@mirinae/lessons/assets/images/songIcon.svg';
import FilmSVG from '@mirinae/lessons/assets/images/filmIcon.svg';
import { HelpBubble, registerHelp } from '@mirinae/lessons/components/lesson/Help';

const CultureExampleElement = styled(Element)`
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
`;

const ExampleLine = styled.div`
    position: relative;
    display: flex;
    flex-direction: row;
    align-items: center;
    ${!os.isOthers && `
        flex-wrap: wrap;
        justify-content: center;
    `}
`;

const Prefix = styled.div`
    padding: 0 5px;
    color: #3A3A3A;
`;

const Postfix = styled.div`
    padding-left: 5px;
`;

const ExampleButton = styled(LessonButton)`
    height: 30px;
    padding: 5px 10px;
    border-radius: 15px;
    font-size: 14px;
    font-weight: 700;
    font-family: ${fontkoKR};
    color: ${lessonsPalette.darken[0]};
    background-color: ${lessonsPalette.light[0]};

    &:hover:not(:disabled) {
        color: ${lessonsPalette.primary.white};
        background-color: ${lessonsPalette.darken[0]};
    }
    &:disabled {
        color: ${lessonsPalette.primary.white};
        background-color: ${lessonsPalette.grey[5]};
        svg path {
            fill: ${lessonsPalette.primary.white};
        }
    }
`;
const ExplanationBubble = styled.div`
    z-index: ${zIndex.hint};
    position: absolute;
    left: ${props => `${props.x || 0}px`};
    top: 40px;
    min-width: 310px;
    padding: 10px 15px;
    border: 1px solid ${lessonsPalette.darken[0]};
    border-radius: 10px;
    color: ${lessonsPalette.primary.black};
    font-size: 14px;
    font-weight: 400;
    line-height: 150%;
    background-color: ${lessonsPalette.primary.white};
    box-shadow: 0 4px 4px rgba(0, 0, 0, 0.11);
    transform: translateX(-50%);

    &:before {
        content: '';
        display: block;
        position: absolute;
        left: 50%;
        top: -7px;
        width: 11px;
        height: 11px;
        margin-left: -9px;
        border: solid ${lessonsPalette.darken[1]};
        border-width: 1px 1px 0 0;
        background-color: ${lessonsPalette.primary.white};
        transform: rotate(-45deg);
    }
`;

const CultureExample = ({ element }) => {
    const wrapper = useRef(null);
    const exampleBtn = useRef(null);
    const [explainState, setExplainState] = useState(null);
    const [dist, setDist] = useState(0);

    const handleCloseBubble = (e) => {
        if (e.target !== exampleBtn.current) {
            setExplainState('');
        }
    };

    useEffect(() => {
        document.body.addEventListener('click', handleCloseBubble, false);
        return () => {
            document.body.removeEventListener('click', handleCloseBubble);
        };
    }, []);
    useEffect(() => {
        setDist(exampleBtn.current.getBoundingClientRect().x - wrapper.current.getBoundingClientRect().x + exampleBtn.current.offsetWidth * 0.5);
    }, [explainState]);

    return (
        <CultureExampleElement element={element}>
            <ExampleLine ref={wrapper}>
                { element.fieldsByName.mediaType.value === 'Song' ? <img src={SongSVG} alt="Song" /> : <img src={FilmSVG} alt="Film" /> }
                { element.fieldsByName.prefix.value && (
                    <Prefix>
                        <FormattedText text={element.fieldsByName.prefix.value} styles={element.fieldsByName.prefix.styles} links={element.fieldsByName.prefix.links} />
                    </Prefix>
                )}
                <ExampleButton
                    ref={exampleBtn} className="culture-example"
                    state={explainState}
                    onClick={() => setExplainState(explainState === 'clicked' ? '' : 'clicked')}
                    onMouseEnter={() => setExplainState(explainState || 'hover')}
                    onMouseLeave={() => setExplainState(explainState === 'hover' ? '' : explainState)}
                >
                    {element.fieldsByName.example.value}
                </ExampleButton>
                { element.fieldsByName.postfix.value && (
                    <Postfix>
                        <FormattedText text={element.fieldsByName.postfix.value} styles={element.fieldsByName.postfix.styles} links={element.fieldsByName.postfix.links} />
                    </Postfix>
                )}
                { explainState && (
                    <ExplanationBubble x={dist}>
                        <FormattedText text={element.fieldsByName.explanation.value} styles={element.fieldsByName.explanation.styles} links={element.fieldsByName.explanation.links} />
                    </ExplanationBubble>
                )}
            </ExampleLine>
        </CultureExampleElement>
    );
};

registerHelp(CultureExample, [
    <HelpBubble
        title="Pattern example in Korean culture"
        desktop={{ anchor: '.culture-example', titleAt: 'bottom', arrowPos: 'left', arrowDir: 'down', style: { bottom: 37, left: 25, contentWidth: 280 } }}>
        <p>The lesson&apos;s pattern is used in the song or movie or drama described here.</p>
        <p>Click on the highlighted title for a description of the song or video.  The next page will play a clip containing an example the lesson&apos;s pattern</p>.
    </HelpBubble>,
    <HelpBubble
        title="Korean culture example"
        mobile={{ anchor: '.culture-example', titleAt: 'top', arrowPos: 'left', arrowDir: 'up', style: { top: 37, left: -50, contentWidth: 200 } }}>
        <p>The lesson&apos;s pattern is used in the song or movie or drama described here.</p>
        <p>Click on the highlighted title for a description of the song or video.  The next page will play a clip containing an example the lesson&apos;s pattern</p>.
    </HelpBubble>,
]);

export default CultureExample;
